import { unref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { NoindexSeo, Seo } from '../model/schemas';
import { createSeoLinks } from '../utils/createSeoLinks';
import { createSeoMeta } from '../utils/createSeoMeta';
import { createSeoNoindex } from '../utils/createSeoNoindex';
import { createSeoScripts } from '../utils/createSeoScripts';
import { getSeoRobots } from '../utils/getSeoRobots';
import { isNoindexRobots } from '../utils/isNoindexRobots';

type UseSeoHeadOptions = {
  ampLink?: boolean;
};

type SeoInput = Seo | NoindexSeo;
type SeoInputSource = SeoInput | Ref<SeoInput> | ComputedRef<SeoInput>;

export function useSeoHead(seoSource: SeoInputSource, options: UseSeoHeadOptions = {}) {
  if (import.meta.client) return;

  const route = useRoute();
  const { $isAMP } = useNuxtApp();

  useHead(
    computed(() => {
      const seo = unref(seoSource);
      const robots = getSeoRobots(seo.robots, route.name);
      if (isNoindexRobots(robots) || !isFullSeo(seo)) return createSeoNoindex(seo.title, robots);

      const link = createSeoLinks(seo, { shouldAddAmpLink: Boolean(!$isAMP && options.ampLink) });
      const meta = createSeoMeta(seo, robots);
      const script = createSeoScripts(seo, { singleSchema: $isAMP });

      return {
        title: seo.title,
        meta,
        link,
        script,
      };
    }),
  );
}

function isFullSeo(seo: SeoInput): seo is Seo {
  return 'canonical' in seo && 'og_image' in seo;
}
